import type { NamespaceCatalog } from '@/i18n/ns';

export type CompanyErrorsKey =
  | 'loadFailed'
  | 'saveFailed'
  | 'profileUnavailable'
  | 'requestPersonAssigned'
  | 'requestOwnership'
  | 'requestInviteEmail'
  | 'requestPipedream'
  | 'requestChanged'
  | 'requestForbidden'
  | 'requestGeneric';

const catalog = {
  en: {
    loadFailed: 'We could not load your company. Please try again.',
    saveFailed: 'Could not save your company.',
    profileUnavailable: 'Your company profile is not available.',
    requestPersonAssigned: 'This person already belongs to an organisation.',
    requestOwnership: 'Transfer ownership before removing or demoting an Owner.',
    requestInviteEmail: 'Enter a valid email address.',
    requestPipedream:
      'This Pipedream account can be authorized, but the current plan does not allow tool calls in production. Ask an admin to enable Pipedream Connect.',
    requestChanged: 'This information changed. Reload before saving again.',
    requestForbidden: 'Your account does not have access to this action.',
    requestGeneric: 'We could not complete this request. Please try again.',
  },
  bg: {
    loadFailed: 'Не успяхме да заредим вашата компания. Моля, опитайте отново.',
    saveFailed: 'Не успяхме да запазим вашата компания.',
    profileUnavailable: 'Профилът на вашата компания не е наличен.',
    requestPersonAssigned: 'Този човек вече принадлежи към организация.',
    requestOwnership:
      'Прехвърлете собствеността, преди да премахнете собственик или да понижите ролята му.',
    requestInviteEmail: 'Въведете валиден имейл адрес.',
    requestPipedream:
      'Този акаунт в Pipedream може да бъде упълномощен, но текущият план не позволява извиквания на инструменти в продукционна среда. Помолете администратор да активира Pipedream Connect.',
    requestChanged: 'Тази информация се промени. Презаредете страницата, преди да запазите отново.',
    requestForbidden: 'Вашият акаунт няма достъп до това действие.',
    requestGeneric: 'Не успяхме да изпълним тази заявка. Моля, опитайте отново.',
  },
  hr: {
    loadFailed: 'Nismo mogli učitati vašu tvrtku. Pokušajte ponovno.',
    saveFailed: 'Tvrtku nije moguće spremiti.',
    profileUnavailable: 'Profil vaše tvrtke nije dostupan.',
    requestPersonAssigned: 'Ova osoba već pripada organizaciji.',
    requestOwnership: 'Prenesite vlasništvo prije uklanjanja vlasnika ili promjene njegove uloge.',
    requestInviteEmail: 'Unesite valjanu adresu e-pošte.',
    requestPipedream:
      'Ovaj Pipedream račun može se autorizirati, ali trenutačni plan ne dopušta pozive alata u produkciji. Zatražite od administratora da omogući Pipedream Connect.',
    requestChanged:
      'Ove su se informacije promijenile. Ponovno ih učitajte prije ponovnog spremanja.',
    requestForbidden: 'Vaš račun nema pristup ovoj radnji.',
    requestGeneric: 'Nismo mogli dovršiti ovaj zahtjev. Pokušajte ponovno.',
  },
  cs: {
    loadFailed: 'Vaši společnost se nepodařilo načíst. Zkuste to prosím znovu.',
    saveFailed: 'Společnost se nepodařilo uložit.',
    profileUnavailable: 'Profil vaší společnosti není k dispozici.',
    requestPersonAssigned: 'Tato osoba již patří k organizaci.',
    requestOwnership: 'Před odebráním nebo změnou role vlastníka převeďte vlastnictví.',
    requestInviteEmail: 'Zadejte platnou e-mailovou adresu.',
    requestPipedream:
      'Tento účet Pipedream lze autorizovat, ale aktuální tarif neumožňuje v produkci volání nástrojů. Požádejte administrátora o povolení Pipedream Connect.',
    requestChanged: 'Tyto informace se změnily. Před dalším uložením stránku znovu načtěte.',
    requestForbidden: 'Váš účet nemá k této akci přístup.',
    requestGeneric: 'Tento požadavek se nepodařilo dokončit. Zkuste to prosím znovu.',
  },
  da: {
    loadFailed: 'Vi kunne ikke indlæse din virksomhed. Prøv igen.',
    saveFailed: 'Kunne ikke gemme din virksomhed.',
    profileUnavailable: 'Din virksomhedsprofil er ikke tilgængelig.',
    requestPersonAssigned: 'Denne person tilhører allerede en organisation.',
    requestOwnership: 'Overfør ejerskabet, før du fjerner eller ændrer en ejers rolle.',
    requestInviteEmail: 'Angiv en gyldig e-mailadresse.',
    requestPipedream:
      'Denne Pipedream-konto kan godkendes, men den aktuelle plan tillader ikke værktøjskald i produktion. Bed en administrator om at aktivere Pipedream Connect.',
    requestChanged: 'Disse oplysninger er blevet ændret. Genindlæs, før du gemmer igen.',
    requestForbidden: 'Din konto har ikke adgang til denne handling.',
    requestGeneric: 'Vi kunne ikke gennemføre denne anmodning. Prøv igen.',
  },
  nl: {
    loadFailed: 'We konden je bedrijf niet laden. Probeer het opnieuw.',
    saveFailed: 'Je bedrijf kon niet worden opgeslagen.',
    profileUnavailable: 'Je bedrijfsprofiel is niet beschikbaar.',
    requestPersonAssigned: 'Deze persoon hoort al bij een organisatie.',
    requestOwnership:
      'Draag het eigenaarschap over voordat je een eigenaar verwijdert of degradeert.',
    requestInviteEmail: 'Voer een geldig e-mailadres in.',
    requestPipedream:
      'Dit Pipedream-account kan worden geautoriseerd, maar het huidige abonnement staat geen toolaanroepen in productie toe. Vraag een beheerder om Pipedream Connect in te schakelen.',
    requestChanged:
      'Deze informatie is gewijzigd. Laad de pagina opnieuw voordat je opnieuw opslaat.',
    requestForbidden: 'Je account heeft geen toegang tot deze actie.',
    requestGeneric: 'We konden dit verzoek niet voltooien. Probeer het opnieuw.',
  },
  et: {
    loadFailed: 'Me ei saanud teie ettevõtet laadida. Proovige uuesti.',
    saveFailed: 'Ettevõtte salvestamine ebaõnnestus.',
    profileUnavailable: 'Teie ettevõtte profiil pole saadaval.',
    requestPersonAssigned: 'See inimene kuulub juba organisatsiooni.',
    requestOwnership: 'Enne omaniku eemaldamist või tema rolli muutmist andke omandiõigus üle.',
    requestInviteEmail: 'Sisestage kehtiv e-posti aadress.',
    requestPipedream:
      'Selle Pipedreami konto saab autoriseerida, kuid praegune pakett ei luba tootmiskeskkonnas tööriistakutseid teha. Paluge administraatoril Pipedream Connect lubada.',
    requestChanged: 'See teave muutus. Enne uuesti salvestamist laadige leht uuesti.',
    requestForbidden: 'Teie kontol puudub sellele toimingule juurdepääs.',
    requestGeneric: 'Me ei saanud seda taotlust täita. Proovige uuesti.',
  },
  fi: {
    loadFailed: 'Yritystäsi ei voitu ladata. Yritä uudelleen.',
    saveFailed: 'Yritystä ei voitu tallentaa.',
    profileUnavailable: 'Yrityksesi profiili ei ole käytettävissä.',
    requestPersonAssigned: 'Tämä henkilö kuuluu jo organisaatioon.',
    requestOwnership:
      'Siirrä omistajuus ennen omistajan poistamista tai hänen oikeuksiensa alentamista.',
    requestInviteEmail: 'Anna kelvollinen sähköpostiosoite.',
    requestPipedream:
      'Tämä Pipedream-tili voidaan valtuuttaa, mutta nykyinen tilaus ei salli työkalukutsuja tuotannossa. Pyydä ylläpitäjää ottamaan Pipedream Connect käyttöön.',
    requestChanged: 'Nämä tiedot muuttuivat. Lataa sivu uudelleen ennen uutta tallennusyritystä.',
    requestForbidden: 'Tililläsi ei ole oikeutta tähän toimintaan.',
    requestGeneric: 'Pyyntöä ei voitu suorittaa loppuun. Yritä uudelleen.',
  },
  fr: {
    loadFailed: 'Nous n’avons pas pu charger votre entreprise. Veuillez réessayer.',
    saveFailed: 'Impossible d’enregistrer votre entreprise.',
    profileUnavailable: 'Le profil de votre entreprise n’est pas disponible.',
    requestPersonAssigned: 'Cette personne appartient déjà à une organisation.',
    requestOwnership:
      'Transférez la propriété avant de supprimer ou de rétrograder un propriétaire.',
    requestInviteEmail: 'Saisissez une adresse e-mail valide.',
    requestPipedream:
      'Ce compte Pipedream peut être autorisé, mais le forfait actuel n’autorise pas les appels d’outils en production. Demandez à un administrateur d’activer Pipedream Connect.',
    requestChanged:
      'Ces informations ont changé. Rechargez la page avant de les enregistrer à nouveau.',
    requestForbidden: 'Votre compte n’a pas accès à cette action.',
    requestGeneric: 'Nous n’avons pas pu traiter cette demande. Veuillez réessayer.',
  },
  de: {
    loadFailed: 'Ihr Unternehmen konnte nicht geladen werden. Bitte versuchen Sie es erneut.',
    saveFailed: 'Ihr Unternehmen konnte nicht gespeichert werden.',
    profileUnavailable: 'Ihr Unternehmensprofil ist nicht verfügbar.',
    requestPersonAssigned: 'Diese Person gehört bereits zu einer Organisation.',
    requestOwnership:
      'Übertragen Sie die Inhaberschaft, bevor Sie einen Inhaber entfernen oder herabstufen.',
    requestInviteEmail: 'Geben Sie eine gültige E-Mail-Adresse ein.',
    requestPipedream:
      'Dieses Pipedream-Konto kann autorisiert werden, aber der aktuelle Tarif erlaubt keine Tool-Aufrufe in der Produktion. Bitten Sie einen Administrator, Pipedream Connect zu aktivieren.',
    requestChanged:
      'Diese Informationen wurden geändert. Laden Sie die Seite neu, bevor Sie erneut speichern.',
    requestForbidden: 'Ihr Konto hat keinen Zugriff auf diese Aktion.',
    requestGeneric:
      'Diese Anfrage konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut.',
  },
  el: {
    loadFailed: 'Δεν ήταν δυνατή η φόρτωση της εταιρείας σας. Δοκιμάστε ξανά.',
    saveFailed: 'Δεν ήταν δυνατή η αποθήκευση της εταιρείας σας.',
    profileUnavailable: 'Το προφίλ της εταιρείας σας δεν είναι διαθέσιμο.',
    requestPersonAssigned: 'Αυτό το άτομο ανήκει ήδη σε έναν οργανισμό.',
    requestOwnership: 'Μεταβιβάστε την ιδιοκτησία πριν αφαιρέσετε ή υποβιβάσετε έναν κάτοχο.',
    requestInviteEmail: 'Εισαγάγετε μια έγκυρη διεύθυνση email.',
    requestPipedream:
      'Αυτός ο λογαριασμός Pipedream μπορεί να εξουσιοδοτηθεί, αλλά το τρέχον πρόγραμμα δεν επιτρέπει κλήσεις εργαλείων σε περιβάλλον παραγωγής. Ζητήστε από έναν διαχειριστή να ενεργοποιήσει το Pipedream Connect.',
    requestChanged: 'Αυτές οι πληροφορίες άλλαξαν. Φορτώστε ξανά τη σελίδα πριν αποθηκεύσετε ξανά.',
    requestForbidden: 'Ο λογαριασμός σας δεν έχει πρόσβαση σε αυτήν την ενέργεια.',
    requestGeneric: 'Δεν ήταν δυνατή η ολοκλήρωση αυτού του αιτήματος. Δοκιμάστε ξανά.',
  },
  hu: {
    loadFailed: 'Nem sikerült betölteni a vállalatot. Próbálja újra.',
    saveFailed: 'Nem sikerült menteni a vállalatot.',
    profileUnavailable: 'A vállalati profilja nem érhető el.',
    requestPersonAssigned: 'Ez a személy már egy szervezethez tartozik.',
    requestOwnership: 'A Tulajdonos eltávolítása vagy lefokozása előtt ruházza át a tulajdonjogot.',
    requestInviteEmail: 'Adjon meg érvényes e-mail-címet.',
    requestPipedream:
      'Ez a Pipedream-fiók engedélyezhető, de a jelenlegi csomag nem teszi lehetővé az éles környezetben történő eszközhívásokat. Kérje meg a rendszergazdát a Pipedream Connect engedélyezésére.',
    requestChanged:
      'Ezek az adatok megváltoztak. A mentés újbóli megkísérlése előtt töltse be újra az oldalt.',
    requestForbidden: 'Fiókja nem fér hozzá ehhez a művelethez.',
    requestGeneric: 'Nem sikerült végrehajtani a kérést. Próbálja újra.',
  },
  ga: {
    loadFailed: 'Ní rabhamar in ann do chuideachta a luchtú. Bain triail eile as le do thoil.',
    saveFailed: 'Níorbh fhéidir do chuideachta a shábháil.',
    profileUnavailable: 'Níl próifíl do chuideachta ar fáil.',
    requestPersonAssigned: 'Baineann an duine seo le heagraíocht cheana féin.',
    requestOwnership:
      'Aistrigh an úinéireacht sula mbaintear úinéir nó sula ndéantar é a íosghrádú.',
    requestInviteEmail: 'Cuir isteach seoladh ríomhphoist bailí.',
    requestPipedream:
      'Is féidir an cuntas Pipedream seo a údarú, ach ní cheadaíonn an plean reatha glaonna uirlisí sa táirgeadh. Iarr ar riarthóir Pipedream Connect a chumasú.',
    requestChanged: 'Athraíodh an fhaisnéis seo. Athlódáil sula sábhálfaidh tú arís.',
    requestForbidden: 'Níl rochtain ag do chuntas ar an ngníomh seo.',
    requestGeneric:
      'Ní rabhamar in ann an t-iarratas seo a chomhlánú. Bain triail eile as le do thoil.',
  },
  it: {
    loadFailed: 'Non è stato possibile caricare la tua azienda. Riprova.',
    saveFailed: 'Non è stato possibile salvare la tua azienda.',
    profileUnavailable: 'Il profilo della tua azienda non è disponibile.',
    requestPersonAssigned: "Questa persona appartiene già a un'organizzazione.",
    requestOwnership: 'Trasferisci la proprietà prima di rimuovere o declassare un proprietario.',
    requestInviteEmail: 'Inserisci un indirizzo email valido.',
    requestPipedream:
      'Questo account Pipedream può essere autorizzato, ma il piano attuale non consente chiamate agli strumenti in produzione. Chiedi a un amministratore di abilitare Pipedream Connect.',
    requestChanged:
      'Queste informazioni sono cambiate. Ricarica la pagina prima di salvare di nuovo.',
    requestForbidden: 'Il tuo account non ha accesso a questa azione.',
    requestGeneric: 'Non è stato possibile completare la richiesta. Riprova.',
  },
  lv: {
    loadFailed: 'Neizdevās ielādēt jūsu uzņēmumu. Lūdzu, mēģiniet vēlreiz.',
    saveFailed: 'Neizdevās saglabāt jūsu uzņēmumu.',
    profileUnavailable: 'Jūsu uzņēmuma profils nav pieejams.',
    requestPersonAssigned: 'Šī persona jau pieder organizācijai.',
    requestOwnership: 'Pirms īpašnieka noņemšanas vai lomas pazemināšanas nododiet īpašumtiesības.',
    requestInviteEmail: 'Ievadiet derīgu e-pasta adresi.',
    requestPipedream:
      'Šo Pipedream kontu var autorizēt, taču pašreizējais plāns neļauj izsaukt rīkus produkcijas vidē. Lūdziet administratoram iespējot Pipedream Connect.',
    requestChanged:
      'Šī informācija ir mainījusies. Pirms atkārtotas saglabāšanas atkārtoti ielādējiet lapu.',
    requestForbidden: 'Jūsu kontam nav piekļuves šai darbībai.',
    requestGeneric: 'Neizdevās izpildīt šo pieprasījumu. Lūdzu, mēģiniet vēlreiz.',
  },
  lt: {
    loadFailed: 'Nepavyko įkelti jūsų įmonės. Bandykite dar kartą.',
    saveFailed: 'Nepavyko išsaugoti jūsų įmonės.',
    profileUnavailable: 'Jūsų įmonės profilis nepasiekiamas.',
    requestPersonAssigned: 'Šis asmuo jau priklauso organizacijai.',
    requestOwnership: 'Prieš pašalindami arba pažemindami savininką, perduokite nuosavybę.',
    requestInviteEmail: 'Įveskite galiojantį el. pašto adresą.',
    requestPipedream:
      'Šią „Pipedream“ paskyrą galima autorizuoti, tačiau dabartinis planas neleidžia produkcijoje iškviesti įrankių. Paprašykite administratoriaus įjungti „Pipedream Connect“.',
    requestChanged:
      'Ši informacija pasikeitė. Prieš išsaugodami dar kartą, įkelkite puslapį iš naujo.',
    requestForbidden: 'Jūsų paskyra neturi prieigos prie šio veiksmo.',
    requestGeneric: 'Nepavyko užbaigti šios užklausos. Bandykite dar kartą.',
  },
  mt: {
    loadFailed: 'Ma stajniex nitgħabbew il-kumpanija tiegħek. Erġa’ pprova.',
    saveFailed: 'Ma stajniex insalvaw il-kumpanija tiegħek.',
    profileUnavailable: 'Il-profil tal-kumpanija tiegħek mhuwiex disponibbli.',
    requestPersonAssigned: 'Din il-persuna diġà tappartjeni għal organizzazzjoni.',
    requestOwnership: 'Ittrasferixxi s-sjieda qabel tneħħi jew tbaxxi lil Sid.',
    requestInviteEmail: 'Daħħal indirizz tal-email validu.',
    requestPipedream:
      'Dan il-kont ta’ Pipedream jista’ jiġi awtorizzat, iżda l-pjan attwali ma jippermettix sejħiet tal-għodod fil-produzzjoni. Itlob lil amministratur jippermetti lil Pipedream Connect.',
    requestChanged: 'Din l-informazzjoni nbidlet. Erġa’ għabbi l-paġna qabel terġa’ ssalva.',
    requestForbidden: 'Il-kont tiegħek m’għandux aċċess għal din l-azzjoni.',
    requestGeneric: 'Ma stajniex inlestu din it-talba. Erġa’ pprova.',
  },
  pl: {
    loadFailed: 'Nie udało się wczytać Twojej firmy. Spróbuj ponownie.',
    saveFailed: 'Nie udało się zapisać Twojej firmy.',
    profileUnavailable: 'Profil Twojej firmy jest niedostępny.',
    requestPersonAssigned: 'Ta osoba należy już do organizacji.',
    requestOwnership: 'Przed usunięciem właściciela lub zmianą jego roli przenieś własność.',
    requestInviteEmail: 'Wprowadź prawidłowy adres e-mail.',
    requestPipedream:
      'To konto Pipedream może zostać autoryzowane, ale obecny plan nie zezwala na wywoływanie narzędzi w środowisku produkcyjnym. Poproś administratora o włączenie Pipedream Connect.',
    requestChanged: 'Te informacje uległy zmianie. Wczytaj je ponownie przed kolejną próbą zapisu.',
    requestForbidden: 'Twoje konto nie ma dostępu do tej czynności.',
    requestGeneric: 'Nie udało się zrealizować tego żądania. Spróbuj ponownie.',
  },
  pt: {
    loadFailed: 'Não foi possível carregar sua empresa. Tente novamente.',
    saveFailed: 'Não foi possível salvar sua empresa.',
    profileUnavailable: 'O perfil da sua empresa não está disponível.',
    requestPersonAssigned: 'Esta pessoa já pertence a uma organização.',
    requestOwnership: 'Transfira a propriedade antes de remover ou rebaixar um proprietário.',
    requestInviteEmail: 'Insira um endereço de e-mail válido.',
    requestPipedream:
      'Esta conta do Pipedream pode ser autorizada, mas o plano atual não permite chamadas de ferramentas em produção. Peça a um administrador para ativar o Pipedream Connect.',
    requestChanged:
      'Estas informações foram alteradas. Recarregue a página antes de salvar novamente.',
    requestForbidden: 'Sua conta não tem acesso a esta ação.',
    requestGeneric: 'Não foi possível concluir esta solicitação. Tente novamente.',
  },
  ro: {
    loadFailed: 'Nu am putut încărca compania. Încercați din nou.',
    saveFailed: 'Compania nu a putut fi salvată.',
    profileUnavailable: 'Profilul companiei nu este disponibil.',
    requestPersonAssigned: 'Această persoană aparține deja unei organizații.',
    requestOwnership:
      'Transferați dreptul de proprietate înainte de a elimina sau retrograda un proprietar.',
    requestInviteEmail: 'Introduceți o adresă de e-mail validă.',
    requestPipedream:
      'Acest cont Pipedream poate fi autorizat, dar planul actual nu permite apeluri de instrumente în producție. Solicitați unui administrator să activeze Pipedream Connect.',
    requestChanged:
      'Aceste informații s-au modificat. Reîncărcați pagina înainte de a salva din nou.',
    requestForbidden: 'Contul dvs. nu are acces la această acțiune.',
    requestGeneric: 'Nu am putut finaliza această solicitare. Încercați din nou.',
  },
  sk: {
    loadFailed: 'Nepodarilo sa načítať vašu spoločnosť. Skúste to znova.',
    saveFailed: 'Nepodarilo sa uložiť vašu spoločnosť.',
    profileUnavailable: 'Profil vašej spoločnosti nie je k dispozícii.',
    requestPersonAssigned: 'Táto osoba už patrí do organizácie.',
    requestOwnership: 'Pred odstránením alebo zmenou roly vlastníka preneste vlastníctvo.',
    requestInviteEmail: 'Zadajte platnú e-mailovú adresu.',
    requestPipedream:
      'Tento účet Pipedream možno autorizovať, ale aktuálny plán nepovoľuje volania nástrojov v produkcii. Požiadajte správcu o povolenie Pipedream Connect.',
    requestChanged: 'Tieto informácie sa zmenili. Pred opätovným uložením stránku znova načítajte.',
    requestForbidden: 'Váš účet nemá prístup k tejto akcii.',
    requestGeneric: 'Túto požiadavku sa nepodarilo dokončiť. Skúste to znova.',
  },
  sl: {
    loadFailed: 'Podjetja ni bilo mogoče naložiti. Poskusite znova.',
    saveFailed: 'Podjetja ni bilo mogoče shraniti.',
    profileUnavailable: 'Profil vašega podjetja ni na voljo.',
    requestPersonAssigned: 'Ta oseba že pripada organizaciji.',
    requestOwnership: 'Preden odstranite lastnika ali mu znižate vlogo, prenesite lastništvo.',
    requestInviteEmail: 'Vnesite veljaven e-poštni naslov.',
    requestPipedream:
      'Ta račun Pipedream je mogoče avtorizirati, vendar trenutni paket ne dovoljuje klicev orodij v produkciji. Prosite skrbnika, naj omogoči Pipedream Connect.',
    requestChanged:
      'Te informacije so se spremenile. Pred ponovnim shranjevanjem jih znova naložite.',
    requestForbidden: 'Vaš račun nima dostopa do tega dejanja.',
    requestGeneric: 'Te zahteve ni bilo mogoče dokončati. Poskusite znova.',
  },
  es: {
    loadFailed: 'No pudimos cargar tu empresa. Inténtalo de nuevo.',
    saveFailed: 'No se pudo guardar tu empresa.',
    profileUnavailable: 'El perfil de tu empresa no está disponible.',
    requestPersonAssigned: 'Esta persona ya pertenece a una organización.',
    requestOwnership: 'Transfiere la propiedad antes de eliminar o degradar a un propietario.',
    requestInviteEmail: 'Introduce una dirección de correo electrónico válida.',
    requestPipedream:
      'Esta cuenta de Pipedream puede autorizarse, pero el plan actual no permite realizar llamadas a herramientas en producción. Pide a un administrador que habilite Pipedream Connect.',
    requestChanged: 'Esta información ha cambiado. Vuelve a cargarla antes de guardarla de nuevo.',
    requestForbidden: 'Tu cuenta no tiene acceso a esta acción.',
    requestGeneric: 'No pudimos completar esta solicitud. Inténtalo de nuevo.',
  },
  sv: {
    loadFailed: 'Vi kunde inte läsa in ditt företag. Försök igen.',
    saveFailed: 'Det gick inte att spara ditt företag.',
    profileUnavailable: 'Din företagsprofil är inte tillgänglig.',
    requestPersonAssigned: 'Den här personen tillhör redan en organisation.',
    requestOwnership: 'Överför ägarskapet innan du tar bort en ägare eller ändrar en ägares roll.',
    requestInviteEmail: 'Ange en giltig e-postadress.',
    requestPipedream:
      'Det här Pipedream-kontot kan auktoriseras, men den aktuella planen tillåter inte verktygsanrop i produktion. Be en administratör att aktivera Pipedream Connect.',
    requestChanged: 'Den här informationen har ändrats. Läs in sidan igen innan du sparar på nytt.',
    requestForbidden: 'Ditt konto har inte åtkomst till den här åtgärden.',
    requestGeneric: 'Vi kunde inte slutföra den här begäran. Försök igen.',
  },
} as const satisfies NamespaceCatalog<CompanyErrorsKey>;

export default catalog;
